/**
 * Forme minimale d'un compte telle que consommée par les sélecteurs.
 * Les champs sont optionnels : les données proviennent de sources hétérogènes
 * (API, arbre de comptes aplati, import), et le code peut arriver en nombre.
 */
export interface IAccountLike {
  code?: string | number;
  name?: string;
  /** Libellé localisé, servi en snake_case par l'API… */
  localized_name?: string;
  /** …ou en camelCase après normalisation côté client. */
  localizedName?: string;
}

/**
 * Get the display name for an account, preferring localized_name if available.
 * Format: "account_code - account_name"
 * @param account - The account object
 * @returns The account display name
 */
export const getAccountDisplayName = (
  account?: IAccountLike | null,
): string => {
  if (!account) {
    return '';
  }
  const name =
    account.localized_name || account.localizedName || account.name || '';
  const code = account.code || '';

  if (code && name) {
    return `${code} - ${name}`;
  }
  return name;
};

/**
 * Ordre d'affichage des comptes : par CODE croissant, comparé caractère par
 * caractère (ordre lexicographique) et non par valeur numérique.
 *
 * Les codes du plan comptable sont HIÉRARCHIQUES : chaque niveau préfixe le
 * suivant. L'ordre lexicographique est le seul qui restitue cette hiérarchie —
 * un collectif précède toujours ses sous-comptes, et deux frères se suivent :
 *
 *   6111 < 611104 < 6112 < 6131 < 6183 < 6197
 *
 * Une comparaison « numérique » (localeCompare avec `numeric: true`) casserait
 * cet ordre : elle lit 611104 comme un entier et le range après 6112.
 * Les comptes sans code sont renvoyés en fin de liste.
 */
/**
 * Rang de la classe CGNC dans le sélecteur.
 *
 * Trié sur le seul code, le sélecteur s'ouvrait sur les 85 comptes de la
 * **classe 0** — « Réouverture des comptes de capitaux propres », « Clôture
 * des comptes… », engagements et crédit-bail hors bilan — puis, tout en bas,
 * les 87 comptes de la **classe 9** (analytique). Ce sont deux familles qu'un
 * comptable n'impute quasiment jamais à la saisie, et elles occupaient la
 * première page d'une liste de près de mille comptes.
 *
 * Elles restent accessibles et gardent leur numéro (le zéro de tête FAIT
 * partie du code CGNC : le retirer produirait 111, 579, 611… qui entrent en
 * collision avec les classes 1, 5 et 6) — elles passent simplement après les
 * classes 1 à 8, celles de la comptabilité générale.
 */
const accountClassRank = (code: string): number => {
  const first = code.charAt(0);
  if (first === '0') return 2; // comptes spéciaux (hors bilan)
  if (first === '9') return 1; // comptabilité analytique
  return 0; // classes 1 à 8 — comptabilité générale
};

export const compareByAccountCode = (
  a?: IAccountLike | null,
  b?: IAccountLike | null,
): number => {
  const codeA = (a?.code ?? '').toString();
  const codeB = (b?.code ?? '').toString();

  if (!codeA && !codeB) {
    return (a?.name ?? '').localeCompare(b?.name ?? '');
  }
  if (!codeA) return 1;
  if (!codeB) return -1;

  const rankA = accountClassRank(codeA);
  const rankB = accountClassRank(codeB);
  if (rankA !== rankB) return rankA - rankB;

  if (codeA === codeB) {
    return (a?.name ?? '').localeCompare(b?.name ?? '');
  }
  return codeA < codeB ? -1 : 1;
};

/**
 * A search query is treated as an account-code lookup when it only contains
 * digits (accounting codes are numeric, e.g. 7111). Spaces around the query
 * are ignored so "  71 " still counts as a code search.
 */
export const isAccountCodeQuery = (query?: string | null): boolean =>
  /^\d+$/.test((query || '').trim());

/**
 * Filters accounts for the account selection fields.
 *
 * When the user types a NUMBER, we match the account code by PREFIX
 * (e.g. "7" surfaces every class-7 account, "711" narrows to 711x) instead of
 * "contains" — so typing "7" no longer brings up 4718, 6147, etc.
 * When the user types TEXT, we keep matching anywhere inside "code - name".
 *
 * La signature (query, item, index, exactMatch) est imposée par le
 * `itemPredicate` de Blueprint.js ; `index` n'est pas utilisé ici.
 */
export const accountPredicate = (
  query: string,
  account?: IAccountLike | null,
  _index?: number,
  exactMatch?: boolean,
): boolean => {
  const normalizedTitle = getAccountDisplayName(account).toLowerCase();
  const normalizedQuery = (query || '').toLowerCase().trim();
  const code = (account?.code ?? '').toString().toLowerCase();

  if (exactMatch) {
    return normalizedTitle === normalizedQuery || code === normalizedQuery;
  }
  if (isAccountCodeQuery(normalizedQuery)) {
    return code.startsWith(normalizedQuery);
  }
  return `${code} ${normalizedTitle}`.indexOf(normalizedQuery) >= 0;
};
